import {
  useRecordContext,
  RecordContextProvider,
  NumberField,
  TabbedShowLayout,
  useTranslate,
} from "react-admin";
import Box from "@mui/material/Box";
import useTaxPostingMode from "../../../hooks/useTaxPostingMode";

const roundHalfUp = (value) => {
  if (value >= 0) {
    return Math.floor(value + 0.5);
  }
  return Math.ceil(value - 0.5);
};

const resolveTaxRate = (taxRate) => {
  const parsedRate = Number(taxRate);
  if (Number.isFinite(parsedRate) && parsedRate >= 0) {
    return parsedRate;
  }
  return 0.11;
};

const calcPurchaseReturnDPP = (item, taxRate) => {
  const qty = Number(item?.return_quantity ?? 0);
  const price = Number(item?.purchase_item?.purchase_order_item?.price ?? 0);
  const disc = Number(item?.purchase_item?.purchase_order_item?.disc ?? 0);
  const unitGross = price - disc;
  const rate = resolveTaxRate(taxRate);
  const denominator = 1 + rate;

  if (!Number.isFinite(unitGross) || !Number.isFinite(qty) || qty <= 0 || denominator <= 0) {
    return 0;
  }

  const unitDPP = roundHalfUp(unitGross / denominator);
  return unitDPP * qty;
};

const calcPurchaseReturnPPN = (item, taxRate) => {
  const subtotal = Number(item?.subtotal ?? 0);
  const dpp = calcPurchaseReturnDPP(item, taxRate);
  if (!Number.isFinite(subtotal)) {
    return 0;
  }
  const roundedSubtotal = roundHalfUp(subtotal);
  return roundedSubtotal - dpp;
};

const PurchaseReturnExpandList = ({ forReport }) => {
  const record = useRecordContext();
  const t = useTranslate();
  const { isManualTaxPosting } = useTaxPostingMode();
  return (
    <TabbedShowLayout
      syncWithLocation={false}
      sx={{
        m: 4,
        backgroundClip: "padding-box",
        boxShadow: (theme) => {
          return theme.components.MuiPaper
            ? theme.components.MuiPaper.styleOverrides.elevation1.boxShadow
            : undefined;
        },
        borderRadius: "4px",
        bgcolor: "background.paper",
        "& .RaTabbedShowLayout-content": {
          p: 0,
        },
        "& .RaLabeled-label": {
          display: "none",
        },
      }}
    >
      <TabbedShowLayout.Tab label={t("mine.label.product")}>
        <Box
          component="table"
          sx={{
            width: "100%",
            // bgcolor: "rgba(0,0,0,.04)",
            // fontSize: "smaller",
            borderCollapse: "collapse",
            "& th, td": {
              padding: "2px 8px",
              border: "1px solid #dddddd",
            },
          }}
        >
          <thead>
            <tr>
              <th>PO. Number</th>
              <th>{t("mine.label.product_code")}</th>
              <th>{t("mine.label.product_name")}</th>
              <th style={{ textAlign: "right" }}>
                {t("mine.label.received_quantity")}
              </th>
              <th style={{ textAlign: "right" }}>
                {t("mine.label.return_quantity")}
              </th>
              <th style={{ textAlign: "right" }}>{t("mine.label.price")}</th>
              {!isManualTaxPosting ? (
                <th style={{ textAlign: "right" }}>DPP</th>
              ) : null}
              {!isManualTaxPosting ? (
                <th style={{ textAlign: "right" }}>PPN</th>
              ) : null}
              <th style={{ textAlign: "right" }}>Subtotal</th>
              <th>{t("mine.label.warehouse")}</th>
            </tr>
          </thead>
          <tbody>
            {record.purchase_return_items.map((item, idx) => {
              const row = isManualTaxPosting
                ? item
                : {
                    ...item,
                    dpp_amount: calcPurchaseReturnDPP(item, record?.tax_rate),
                    tax_amount: calcPurchaseReturnPPN(item, record?.tax_rate),
                  };
              return (
                <tr key={item.id + idx}>
                  <td>{item.purchase_item.purchase.code}</td>
                  <td>{item.purchase_item.purchase_order_item.product.code}</td>
                  <td>{item.purchase_item.purchase_order_item.product.name}</td>
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="purchase_item.received_quantity"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="return_quantity"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="purchase_item.purchase_order_item.price"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                  {!isManualTaxPosting ? (
                    <td style={{ textAlign: "right" }}>
                      <RecordContextProvider value={row}>
                        <NumberField
                          source="dpp_amount"
                          sx={{
                            fontSize: "inherit",
                          }}
                        />
                      </RecordContextProvider>
                    </td>
                  ) : null}
                  {!isManualTaxPosting ? (
                    <td style={{ textAlign: "right" }}>
                      <RecordContextProvider value={row}>
                        <NumberField
                          source="tax_amount"
                          sx={{
                            fontSize: "inherit",
                          }}
                        />
                      </RecordContextProvider>
                    </td>
                  ) : null}
                  <td style={{ textAlign: "right" }}>
                    <RecordContextProvider value={item}>
                      <NumberField
                        source="subtotal"
                        sx={{
                          fontSize: "inherit",
                        }}
                      />
                    </RecordContextProvider>
                  </td>
                  <td>{item.warehouse.name}</td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </TabbedShowLayout.Tab>
      {!forReport && (record.transactions?.length ?? 0) > 0 ? (
        <TabbedShowLayout.Tab label={t("mine.label.journal")}>
          <Box
            component="table"
            sx={{
              width: "100%",
              // bgcolor: "rgba(0,0,0,.04)",
              // fontSize: "smaller",
              borderCollapse: "collapse",
              "& th, td": {
                padding: "6px 8px",
                border: "1px solid #dddddd",
              },
            }}
          >
            <thead>
              <tr>
                <th>COA</th>
                <th>COA Name</th>
                <th>Description</th>
                <th style={{ textAlign: "right" }}>Debit</th>
                <th style={{ textAlign: "right" }}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {(record.transactions ?? []).map((item, idx) => {
                return (
                  <tr key={item.id + idx}>
                    <td>{item.coa.code}</td>
                    <td>{item.coa.name}</td>
                    <td>{item.description}</td>
                    <td style={{ textAlign: "right" }}>
                      <RecordContextProvider value={item}>
                        <NumberField
                          source="debit"
                          sx={{
                            fontSize: "inherit",
                          }}
                        />
                      </RecordContextProvider>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <RecordContextProvider value={item}>
                        <NumberField
                          source="credit"
                          sx={{
                            fontSize: "inherit",
                          }}
                        />
                      </RecordContextProvider>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </TabbedShowLayout.Tab>
      ) : null}
    </TabbedShowLayout>
  );
};

export default PurchaseReturnExpandList;
